import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { Device } from '@capacitor/device';
import { LocalNotifications } from '@capacitor/local-notifications';
import axoisAPI from '../API/api';
import { DevURL } from '../constant/default';

// Define notification data type
interface NotificationData {
  title?: string;
  body?: string;
  id?: string;
  type?: string;
  chatId?: string;
  userId?: string;
  [key: string]: any;
}

export class PushNotificationService {
  // Track if notifications are already initialized
  private static isInitialized = false;

  // Check if we're on a device that supports push notifications
  static isPlatformSupported(): boolean {
    // Consider both native platforms and browsers to be supported
    return true;
  }
  
  // Check if we're on a native platform (Android/iOS)
  static isNativePlatform(): boolean {
    return Capacitor.isNativePlatform();
  }

  // Initialize push notifications
  static async initializePushNotifications(): Promise<void> {
    // Avoid duplicate initialization
    if (this.isInitialized) {
      console.log('ℹ️ Push notifications already initialized');
      return;
    }

    try {
      console.log('🚀 STARTING PUSH NOTIFICATION INITIALIZATION');
      
      // Check if we're on a native platform (Android/iOS) or web browser
      if (this.isNativePlatform()) {
        // Native platform implementation
        console.log('📱 Detected native platform - using Capacitor push notifications');
      
        // Initialize local notifications first
        console.log('📱 Initializing local notifications');
        await this.initializeLocalNotifications();
        
        // Set up event listeners BEFORE registration
        console.log('🔔 Setting up push notification event listeners');
        this.addListeners();
        
        // Request permission to receive push notifications
        console.log('🔒 Requesting push notification permissions');
        const permissionStatus = await PushNotifications.requestPermissions();
        console.log('🔑 Permission status:', permissionStatus);
        
        if (permissionStatus.receive !== 'granted') {
          console.warn('❌ Push notification permission was not granted');
          return;
        }
        
        console.log('✅ PERMISSION GRANTED - REGISTERING FOR PUSH NOTIFICATIONS');
        
        // Register for push notifications
        await PushNotifications.register();
        
        // Clear any existing notifications
        await PushNotifications.removeAllDeliveredNotifications();
        
        // Wait a moment for the token to potentially be generated
        console.log('⏳ Waiting for token generation...');
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        this.isInitialized = true;
        console.log('✅ Push notification initialization complete - waiting for token event');
        
        // Manually check token status after a delay
        setTimeout(async () => {
          console.log('🔍 Checking FCM token status after delay');
          const token = await this.getExistingPushToken();
          if (token) {
            console.log('✅ FCM TOKEN FOUND AFTER DELAY:', token);
            // Register this token with the backend
            await this.registerTokenWithBackend(token);
          } else {
            console.log('⚠️ NO FCM TOKEN AVAILABLE EVEN AFTER DELAY');
          }
        }, 5000);  // 5 second delay
      } else {
        // Web browser implementation
        console.log('🌐 Detected web browser - using Web Notifications API');
        
        // Request permission for browser notifications
        if (!('Notification' in window)) {
          console.warn('❌ This browser does not support web notifications');
          return;
        }
        
        const permission = await Notification.requestPermission();
        console.log('🔑 Browser notification permission status:', permission);
        
        if (permission !== 'granted') {
          console.warn('❌ Browser notification permission was not granted');
          return;
        }
        
        console.log('✅ BROWSER NOTIFICATION PERMISSION GRANTED');
        this.isInitialized = true;
      }
    } catch (error) {
      console.error('❌ ERROR INITIALIZING PUSH NOTIFICATIONS:', error);
      // Try to provide more details about the error
      if (error instanceof Error) {
        console.error('❌ Error name:', error.name);
        console.error('❌ Error message:', error.message);
        console.error('❌ Error stack:', error.stack);
      }
    }
  }

  // Initialize local notifications
  private static async initializeLocalNotifications(): Promise<void> {
    try {
      // Request permissions for local notifications
      await LocalNotifications.requestPermissions();
      console.log('Local notifications initialized');
    } catch (error) {
      console.error('Error initializing local notifications:', error);
    }
  }

  // Add listeners for push notification events
  private static addListeners(): void {
    console.log('📣 SETTING UP PUSH NOTIFICATION LISTENERS');
    
    // Called when FCM token is available via standard Capacitor push notifications plugin
    PushNotifications.addListener('registration', 
      async ({ value }) => {
        console.log('🎉🎉🎉 FCM TOKEN RECEIVED EVENT TRIGGERED 🎉🎉🎉');
        console.log('📱 FCM token received:', value);
        console.log('🔥🔥🔥 FIREBASE TOKEN:', value, '🔥🔥🔥');
        
        // Store token in localStorage for debugging purposes
        localStorage.setItem('fcm_token', value);
        console.log('💾 FCM TOKEN SAVED TO LOCAL STORAGE');
        
        // Also store auth token in SharedPreferences for native code to access
        if (Capacitor.isNativePlatform()) {
          try {
            // Get auth token from localStorage
            const authToken = localStorage.getItem('token');
            if (authToken) {
              console.log('💾 Storing auth token in SharedPreferences for native code');
              // Use Storage API (now Preferences) to store the token
              try {
                import('@capacitor/preferences').then(async module => {
                  await module.Preferences.set({
                    key: "AuthToken",
                    value: authToken
                  });
                  console.log('Stored auth token using Preferences API');
                });
              } catch (err) {
                console.error('Error storing with Preferences API:', err);
              }
              
              // Use FCMBridge plugin to save auth token directly
              try {
                console.log('Trying to save auth token using FCMBridge plugin');
                // Access FCMBridge plugin
                if (Capacitor.isPluginAvailable('FCMBridge')) {
                  console.log('FCMBridge plugin available, saving auth token');
                  // Use the plugin to save the token
                  const plugins = Capacitor as any;
                  if (plugins.Plugins && plugins.Plugins.FCMBridge) {
                    plugins.Plugins.FCMBridge.saveAuthToken({
                      authToken: authToken
                    });
                    console.log('★★★★★ Auth token saved using FCMBridge plugin ★★★★★');
                  } else {
                    console.log('FCMBridge plugin object not accessible');
                  }
                } else {
                  console.log('FCMBridge plugin not available');
                }
              } catch (err) {
                console.log('Error saving auth token via FCMBridge plugin:', err);
              }
            }
          } catch (err) {
            console.error('Error storing token in SharedPreferences:', err);
          }
        }
        
        try {
          // Register the token with our backend
          console.log('🚀 ABOUT TO REGISTER TOKEN WITH BACKEND');
          await this.registerTokenWithBackend(value);
          console.log('✅ BACKEND REGISTRATION FUNCTION FULLYYY COMPLETED');
          localStorage.setItem('fcm_token_registered', 'true');
        } catch (error) {
          console.error('❌ ERROR IN REGISTRATION LISTENER:', error);
          localStorage.setItem('fcm_token_registration_error', JSON.stringify(error));
        }
      }
    );
    
    // Set up listener for local notification actions (for in-app notifications)
    try {
      console.log('🔔 SETTING UP LOCAL NOTIFICATION ACTION LISTENER');
      LocalNotifications.addListener('localNotificationActionPerformed', 
        (notification) => {
          console.log('📱 In-app notification clicked:', notification);
          // Use the same handler as push notifications for consistency
          this.handleNotificationAction(notification);
        }
      );
      console.log('✅ LOCAL NOTIFICATION ACTION LISTENER REGISTERED');
    } catch (error) {
      console.error('❌ ERROR SETTING UP LOCAL NOTIFICATION LISTENER:', error);
    }
    
    // Add listener for our custom FCM token event from native layer
    if (Capacitor.isNativePlatform()) {
      console.log('📱 SETTING UP CUSTOM FCM TOKEN LISTENER');
      
      // Listen for window event from native code
      window.addEventListener('fcmTokenReceived', async (event: any) => {
        try {
          console.log('🎉🎉🎉 CUSTOM FCM TOKEN EVENT RECEIVED 🎉🎉🎉');
          let tokenData;
          
          // Parse the event data
          if (event.detail) {
            try {
              tokenData = JSON.parse(event.detail);
            } catch (e) {
              tokenData = event.detail;
            }
          } else {
            tokenData = event;
          }
          
          if (tokenData && tokenData.token) {
            const token = tokenData.token;
            console.log('📱 Custom FCM token received:', token);
            
            // Store token in localStorage
            localStorage.setItem('fcm_token', token);
            console.log('💾 CUSTOM FCM TOKEN SAVED TO LOCAL STORAGE');
            
            // Register with backend
            try {
              await this.registerTokenWithBackend(token);
              console.log('✅ CUSTOM TOKEN BACKEND REGISTRATION COMPLETED');
              localStorage.setItem('fcm_token_registered', 'true');
            } catch (error) {
              console.error('❌ ERROR IN CUSTOM TOKEN REGISTRATION:', error);
            }
          } else {
            console.error('❌ RECEIVED CUSTOM FCM EVENT BUT NO TOKEN FOUND');
          }
        } catch (error) {
          console.error('❌ ERROR HANDLING CUSTOM FCM TOKEN EVENT:', error);
        }
      });
      
      // Set up listener for plugin events
      if (Capacitor.isPluginAvailable('FCMBridge')) {
        try {
          console.log('📱 SETTING UP FCM BRIDGE PLUGIN LISTENER');
          
          // Access the plugin through the window object to avoid TypeScript errors
          const plugins = (Capacitor as any).Plugins;
          if (plugins && plugins.FCMBridge) {
            plugins.FCMBridge.addListener('fcmTokenReceived', async (data: any) => {
              console.log('🎉 FCM BRIDGE TOKEN EVENT:', data);
              if (data && data.token) {
                // Store token
                localStorage.setItem('fcm_token', data.token);
                console.log('💾 FCM BRIDGE TOKEN SAVED:', data.token.substring(0, 10) + '...');
                
                // Register with backend
                try {
                  await this.registerTokenWithBackend(data.token);
                  console.log('✅ FCM BRIDGE TOKEN REGISTRATION COMPLETED');
                } catch (error) {
                  console.error('❌ ERROR REGISTERING FCM BRIDGE TOKEN:', error);
                }
              }
            });
            console.log('✅ FCM BRIDGE LISTENER REGISTERED');
          } else {
            console.warn('⚠️ FCM BRIDGE PLUGIN AVAILABLE BUT PLUGINS OBJECT MISSING');
          }
        } catch (pluginError) {
          console.error('❌ ERROR SETTING UP FCM BRIDGE LISTENER:', pluginError);
        }
      }
    }
    
    // Called when a notification is received while app is in foreground
    PushNotifications.addListener('pushNotificationReceived', 
      (notification) => {
        console.log('Notification received in foreground:', notification);
        // Process the notification data
        this.processNotificationData(notification);
        
        // Show local notification for foreground notifications
        this.showLocalNotification(notification);
      }
    );
    
    // Called when a notification is tapped
    PushNotifications.addListener('pushNotificationActionPerformed', 
      (notification) => {
        console.log('🔥 pushNotificationActionPerformed received:', notification);
        
        // Extract notification data
        const notificationData = notification?.notification?.data || {};
        
        // Trigger API from JS for call notifications if not already done
        if (notificationData.type === 'CallConnecting') {
          console.log('📡 JS side detected CallConnecting notification - ensuring API call is made');
          
          try {
            // Set a flag to indicate this call is coming from a push notification (to avoid duplicate ringtones)
            localStorage.setItem('call_from_push_notification', 'true');
            
            // Get necessary data for API call
            const receiverId = notificationData.receiverId;
            const callType = notificationData.callType;
            const senderId = notificationData.senderId;
            const callConnectionId = notificationData.callConnectionId;
            
            // Get auth token
            const authToken = localStorage.getItem('token');
            
            if (receiverId && authToken) {
              console.log('📡 JS retrying PUT /incoming-call/detect');
              
              // Define the type for the request body
              interface CallDetectionRequestBody {
                callConnectionId: string;
                incomingCommunicationUserId: string;
                [key: string]: any; // Allow any additional properties
              }
              
              // Build the correct API body format
              const body: CallDetectionRequestBody = {
                callConnectionId: callConnectionId || "",
                incomingCommunicationUserId: senderId || ""
              };
              
              // Add original values for debugging if API fails
              if (callType) {
                body.originalCallType = callType;
              }
              
              console.log('📡 Making API call with body:', body);
              
              // Make API call
              fetch(`${DevURL}commapi/incoming-call/detect?user-id=${receiverId}`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify(body)
              })
              .then(response => {
                console.log('📡 API call response status:', response.status);
                return response.json();
              })
              .then(data => {
                console.log('📡 API call response data:', data);
                if (data && data.isSuccess) {
                  console.log('✅ JS side API call successful, triggering incomingCallDetected event');
                  
                  // Dispatch a synthetic event to notify the call agent about the detected call
                  const event = new CustomEvent('incomingCallDetected', {
                    detail: JSON.stringify(data.data)
                  });
                  window.dispatchEvent(event);
                }
              })
              .catch(error => {
                console.error('❌ JS side API call failed:', error);
              });
            } else {
              console.error('❌ Missing required data for API call', { 
                receiverId, 
                hasToken: !!authToken 
              });
            }
          } catch (error) {
            console.error('❌ Error making JS side API call:', error);
          }
        }
        
        // Handle the notification action as normal
        this.handleNotificationAction(notification);
      }
    );
  }

  // Process notification data
  private static processNotificationData(notification: any): NotificationData {
    try {
      let data: NotificationData = {};
      
      // Extract data from notification payload
      if (notification.data) {
        data = notification.data;
      }
      
      // Add title and body if available
      if (notification.title) {
        data.title = notification.title;
      }
      
      if (notification.body) {
        data.body = notification.body;
      }
      
      // Attempt to parse any string data that might be JSON
      if (data.custom && typeof data.custom === 'string') {
        try {
          const customData = JSON.parse(data.custom);
          data = { ...data, ...customData };
        } catch (e) {
          console.log('Could not parse custom data as JSON');
        }
      }
      
      return data;
    } catch (error) {
      console.error('Error processing notification data:', error);
      return {};
    }
  }

  // Show local notification for foreground notifications
  private static async showLocalNotification(notification: any): Promise<void> {
    try {
      const data = this.processNotificationData(notification);
      const title = data.title || notification.title || 'Phone Darlings';
      const body = data.body || notification.body || 'You have a new notification';
      
      if (this.isNativePlatform()) {
        // Native platform implementation using Capacitor
        await LocalNotifications.schedule({
          notifications: [
            {
              id: Math.floor(Math.random() * 100000),
              title: title,
              body: body,
              largeBody: body,
              sound: 'message_tone.mp3',
              smallIcon: 'ic_stat_phonedarlingslogo',
              actionTypeId: data.type || 'notification',
              extra: data
            }
          ]
        });
      } else {
        // Web browser implementation
        if ('Notification' in window && Notification.permission === 'granted') {
          // Play notification sound
          const audio = new Audio('/ringtones/message-tone.mp3');
          audio.play().catch(e => console.error('Error playing notification sound:', e));
          
          // Show browser notification
          const notificationOptions = {
            body: body,
            icon: '/phoneDarlingsFavIcon.svg',
            tag: 'chat-message',
            data: data
          };
          
          const browserNotification = new Notification(title, notificationOptions);
          
          // Handle notification click
          browserNotification.onclick = () => {
            // Focus the window and close the notification
            window.focus();
            browserNotification.close();
            
            // Handle the notification action using our existing method
            this.handleNotificationAction({
              notification: {
                title: title,
                body: body,
                data: data
              }
            });
          };
        } else {
          console.warn('Browser notifications not allowed');
        }
      }
    } catch (error) {
      console.error('Error showing local notification:', error);
    }
  }

  // Handle notification action (when notification is tapped)
  static handleNotificationAction(actionData: any): void {
    try {
      // Extract notification data from either push or local notification structures
      let data;
      
      console.log('🔔 NOTIFICATION TAPPED - FULL DATA:', JSON.stringify(actionData));
      
      // Handle both FCM push notifications and local notifications
      if (actionData.notification) {
        // FCM push notification structure
        console.log('📱 FCM notification structure detected');
        data = this.processNotificationData(actionData.notification);
      } else if (actionData.notification || actionData.actionId) {
        // Local notification structure from Capacitor
        console.log('📱 Local notification structure detected');
        // The notification is either in notification property or at root level
        const notificationData = actionData.notification || actionData;
        // For local notifications, extra data is in the "extra" property
        data = notificationData.extra || this.processNotificationData(notificationData);
      } else {
        // Fallback case if we can't find data in expected structure
        console.log('📱 Using fallback notification data parsing');
        data = this.processNotificationData(actionData);
      }
      
      // Always ensure click_action is set for consistent handling
      data.click_action = data.click_action || "OPEN_CHAT_ACTIVITY";
      
      // Log the notification data for debugging
      console.log('🔔 PROCESSED NOTIFICATION DATA:', JSON.stringify(data));
      
      // Get user role to determine correct chat route
      const role = localStorage.getItem('role');
      let chatRoute = '/user/chat';
      
      if (role === 'ServiceProvider') {
        chatRoute = '/consultant/chat';
      }
      
      console.log('👤 User role:', role);
      console.log('🔗 Base chat route:', chatRoute);
      
      // Always navigate to chat for any notification, with thread ID if available
      console.log('🔔 Processing navigation for notification click');
      
      // Set a flag that we came from notification
      localStorage.setItem('from_notification', 'true');
      console.log('💾 Set from_notification flag in localStorage');
      
      // If we have a threadId, we can navigate directly to specific chat
      if (data.threadId) {
        console.log(`🔔 Notification has threadId ${data.threadId}`);
        
        // Store the thread ID in localStorage to use it in the chat component
        localStorage.setItem('notification_threadId', data.threadId);
        console.log('💾 Saved threadId to localStorage');
        
        // If we have sender ID, also store that for targeting the specific chat
        if (data.senderId) {
          localStorage.setItem('notification_senderId', data.senderId);
          console.log('💾 Saved senderId to localStorage:', data.senderId);
        }
        
        // Log navigation target
        console.log(`🚀 Navigating to ${chatRoute} with threadId ${data.threadId}`);
      } else {
        console.log(`⚠️ Notification doesn't have threadId, navigating to general chat: ${chatRoute}`);
      }
      
      // Store all notification data for potential use in chat component
      localStorage.setItem('notification_data', JSON.stringify(data));
      console.log('💾 Saved full notification data to localStorage');
      
      // Force a small delay before navigation to ensure all logs are processed
      // and localStorage values are set
      console.log('⏳ Small delay before navigation...');
      setTimeout(() => {
        // Navigate to appropriate chat route based on user role
        console.log(`🚀 NAVIGATING TO: ${chatRoute}`);
        window.location.href = chatRoute;
      }, 100);
      
    } catch (error) {
      console.error('❌ ERROR HANDLING NOTIFICATION ACTION:', error);
      
      // Even if there's an error, try to navigate to chat based on role
      const role = localStorage.getItem('role');
      const chatRoute = role === 'ServiceProvider' ? '/consultant/chat' : '/user/chat';
      
      console.log(`⚠️ Error occurred, navigating to ${chatRoute}`);
      window.location.href = chatRoute;
    }
  }

  // Register FCM token with our backend
  private static async registerTokenWithBackend(token: string): Promise<void> {
    try {
      // First check if we have a valid auth token
      const authToken = localStorage.getItem('token');
      
      console.log('🔐 FCM TOKEN REGISTRATION CHECKS:');
      console.log('🔐 Token to register:', token?.substring(0, 10) + '...');
      console.log('🔐 Token length:', token?.length);
      console.log('🔐 Auth token available:', !!authToken);
      console.log('🔐 Auth token value:', authToken?.substring(0, 10) + '...');
      
      if (!authToken) {
        console.log('🛑 NO AUTH TOKEN AVAILABLE - CANNOT REGISTER FCM TOKEN YET');
        console.log('💾 SAVING FCM TOKEN TO LOCAL STORAGE FOR LATER REGISTRATION');
        localStorage.setItem('fcm_token', token);
        return;
      }
      
      // Get the device ID - with extra error handling
      let deviceId = 'unknown-device';
      try {
        const deviceInfo = await Device.getId();
        deviceId = deviceInfo.identifier;
        console.log('📱 SUCCESSFULLY RETRIEVED DEVICE ID:', deviceId);
      } catch (deviceError) {
        console.error('❌ ERROR GETTING DEVICE ID:', deviceError);
        // Fall back to a generated ID if we can't get the device ID
        deviceId = 'fallback-' + Math.random().toString(36).substring(2, 10);
        console.log('📱 USING FALLBACK DEVICE ID:', deviceId);
      }
      
      console.log('🚀 STARTING TOKEN REGISTRATION WITH BACKEND');
      console.log('📱 Device ID:', deviceId);
      console.log('🔑 Token Length:', token.length);
      console.log('🔐 Auth Token Available:', !!authToken);
      
      // Register the token with the backend
      const endpoint = 'authapi/auths/accounts/firebase-token';
      console.log('📡 API Endpoint:', endpoint);
      
      const data = {
        deviceId: deviceId,
        token: token
      };
      console.log('📦 Sending data:', JSON.stringify(data));
      
      // Log the full URL that will be called
      const fullUrl = `${DevURL}${endpoint}`;
      console.log('🔗 FULL API URL:', fullUrl);
      
      console.log('🔄 Making PUT request...');
      // Force a direct API call with explicit logging
      const headers = {
        'authorization': 'Bearer ' + authToken,
        'Content-Type': 'application/json',
        'X-FCM-Registration': 'true' // Custom header for debugging
      };
      console.log('🔒 Request headers:', JSON.stringify(headers));
      
      try {
        const response = await axoisAPI.putwithToken(endpoint, data);
        console.log('✅ API RESPONSE RECEIVED:', response?.status);
        
        if (response && response.data) {
          console.log('✅ FCM TOKEN REGISTERED SUCCESSFULLY:', response.data);
          localStorage.setItem('fcm_token_registration_success', 'true');
        } else {
          console.warn('⚠️ TOKEN REGISTRATION RESPONSE WAS EMPTY');
          localStorage.setItem('fcm_token_registration_empty', 'true');
        }
      } catch (apiError) {
        console.error('❌ API REQUEST FAILED:', apiError);
        throw apiError; // Re-throw to be caught by the outer catch block
      }
    } catch (err) {
      const error = err as any;
      console.error('❌ ERROR REGISTERING TOKEN WITH BACKEND:', error);
      localStorage.setItem('fcm_token_registration_error', JSON.stringify({
        message: error?.message,
        status: error?.response?.status,
        data: error?.response?.data
      }));
      
      // Log more detailed error information
      if (error && error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error('❌ Error response data:', error.response.data);
        console.error('❌ Error response status:', error.response.status);
        console.error('❌ Error response headers:', error.response.headers);
      } else if (error && error.request) {
        // The request was made but no response was received
        console.error('❌ Error request (no response):', error.request);
      } else if (error) {
        // Something happened in setting up the request that triggered an Error
        console.error('❌ Error message:', error.message);
      }
    }
  }
  
  // Public method for registering FCM token during login
  static async registerTokenAtLogin(): Promise<void> {
    console.log('🔄 REGISTER TOKEN AT LOGIN - STARTING');
    
    try {
      if (this.isNativePlatform()) {
        // Native platform implementation
        console.log('📱 NATIVE PLATFORM DETECTED');
        console.log('🔒 REQUESTING PUSH NOTIFICATION PERMISSIONS...');
        
        // Request permission if needed
        const permissionStatus = await PushNotifications.requestPermissions();
        console.log('🔔 PERMISSION STATUS:', permissionStatus);
        
        if (permissionStatus.receive !== 'granted') {
          console.warn('❌ PUSH NOTIFICATION PERMISSION DENIED BY USER');
          return;
        }
        
        console.log('✅ PUSH NOTIFICATION PERMISSION GRANTED');
        console.log('🔄 CHECKING IF NOTIFICATIONS ALREADY INITIALIZED:', this.isInitialized);
        
        // Check if already registered (FCM token already exists)
        if (!this.isInitialized) {
          console.log('🔔 NOT INITIALIZED - REGISTERING FOR PUSH NOTIFICATIONS');
          
          // Register for push notifications
          this.addListeners(); // Add listeners BEFORE registration
          await PushNotifications.register();
          
          // FCM token will be received through the 'registration' event listener
          console.log('🚀 PUSH NOTIFICATION REGISTRATION STARTED - WAITING FOR TOKEN');
          console.log('⚠️ TOKEN WILL BE RECEIVED VIA REGISTRATION EVENT LISTENER');
          
          // Set flag to indicate we've started registration
          this.isInitialized = true;
        } else {
          console.log('✅ ALREADY REGISTERED FOR PUSH NOTIFICATIONS');
          
          // Get existing FCM token
          console.log('🔄 ATTEMPTING TO GET EXISTING TOKEN...');
          const pushToken = await this.getExistingPushToken();
          
          if (pushToken) {
            console.log('✅ EXISTING TOKEN FOUND, REGISTERING WITH BACKEND');
            await this.registerTokenWithBackend(pushToken);
          } else {
            console.log('⚠️ NO EXISTING TOKEN FOUND');
          }
        }
      } else {
        // Web browser implementation
        console.log('🌐 WEB BROWSER DETECTED');
        
        if (!('Notification' in window)) {
          console.warn('❌ This browser does not support web notifications');
          return;
        }
        
        // Request permission if not already granted
        if (Notification.permission !== 'granted') {
          console.log('🔒 REQUESTING BROWSER NOTIFICATION PERMISSION');
          const permission = await Notification.requestPermission();
          
          if (permission !== 'granted') {
            console.warn('❌ BROWSER NOTIFICATION PERMISSION DENIED BY USER');
            return;
          }
        }
        
        console.log('✅ BROWSER NOTIFICATION PERMISSION GRANTED');
        this.isInitialized = true;
      }
    } catch (err) {
      const error = err as Error;
      console.error('❌ ERROR REGISTERING TOKEN AT LOGIN:', error);
      
      if (error instanceof Error) {
        console.error('❌ Error name:', error.name);
        console.error('❌ Error message:', error.message);
        console.error('❌ Error stack:', error.stack);
      }
    }
  }
  
  // Get existing push token if available
  private static async getExistingPushToken(): Promise<string | null> {
    if (!this.isNativePlatform()) {
      return null; // Only relevant for native platforms
    }
    
    try {
      // Check if FCM is ready
      console.log('🔍 ATTEMPTING TO GET EXISTING FCM TOKEN DIRECTLY');
      
      // First check if we have a token stored in localStorage
      const savedToken = localStorage.getItem('fcm_token');
      if (savedToken) {
        console.log('✅ FOUND SAVED TOKEN IN LOCAL STORAGE');
        return savedToken;
      }
      
      // Try to use our custom FCM bridge plugin
      if (Capacitor.isPluginAvailable('FCMBridge')) {
        console.log('📱 FCM BRIDGE PLUGIN DETECTED');
        try {
          // Use the plugin safely with type casting
          const plugins = (Capacitor as any).Plugins;
          if (plugins && plugins.FCMBridge && plugins.FCMBridge.getToken) {
            const result = await plugins.FCMBridge.getToken();
            if (result && result.token) {
              const token = result.token;
              console.log('✅ GOT TOKEN FROM FCM BRIDGE PLUGIN:', token.substring(0, 10) + '...');
              // Store in localStorage for easy access later
              localStorage.setItem('fcm_token', token);
              return token;
            } else {
              console.log('⚠️ FCM BRIDGE RETURNED NO TOKEN');
            }
          } else {
            console.warn('⚠️ FCM BRIDGE PLUGIN AVAILABLE BUT METHOD MISSING');
          }
        } catch (pluginError) {
          console.error('❌ ERROR USING FCM BRIDGE PLUGIN:', pluginError);
        }
      }
      
      // Try to get the native Firebase token if available
      const firebasePlugin = (window as any).FirebasePlugin;
      if (firebasePlugin && typeof firebasePlugin.getToken === 'function') {
        console.log('📱 FIREBASE PLUGIN DETECTED, CALLING getToken()');
        // Direct FirebasePlugin access if available in Capacitor
        return new Promise((resolve, reject) => {
          firebasePlugin.getToken((token: string) => {
            console.log('✅ GOT TOKEN DIRECTLY FROM FIREBASE PLUGIN:', token);
            localStorage.setItem('fcm_token', token);
            resolve(token);
          }, (error: any) => {
            console.error('❌ ERROR GETTING TOKEN FROM FIREBASE PLUGIN:', error);
            reject(error);
          });
        });
      } else {
        console.log('⚠️ FIREBASE PLUGIN NOT DIRECTLY ACCESSIBLE');
      }
      
      // Force a registration to generate a new token
      console.log('🔄 NO DIRECT TOKEN ACCESS, FORCING NEW REGISTRATION');
      return null;
    } catch (error) {
      console.error('❌ ERROR GETTING EXISTING PUSH TOKEN:', error);
      return null;
    }
  }

  // Remove push notification listeners (call when app is unmounted)
  static async removeListeners(): Promise<void> {
    try {
      await PushNotifications.removeAllListeners();
      this.isInitialized = false;
      console.log('Push notification listeners removed');
    } catch (error) {
      console.error('Error removing push notification listeners:', error);
    }
  }
  
  // Unregister FCM token when user logs out
  static async unregisterTokenAtLogout(): Promise<void> {
    console.log('🔄 UNREGISTER TOKEN AT LOGOUT - STARTING');
    
    try {
      if (this.isNativePlatform()) {
        // Native platform implementation
        console.log('📱 NATIVE PLATFORM DETECTED');
        
        // Get the device ID
        let deviceId = 'unknown-device';
        try {
          const deviceInfo = await Device.getId();
          deviceId = deviceInfo.identifier;
          console.log('📱 SUCCESSFULLY RETRIEVED DEVICE ID:', deviceId);
        } catch (deviceError) {
          console.error('❌ ERROR GETTING DEVICE ID:', deviceError);
          // Fall back to a generated ID if we can't get the device ID
          deviceId = 'fallback-' + Math.random().toString(36).substring(2, 10);
          console.log('📱 USING FALLBACK DEVICE ID:', deviceId);
        }
        
        // Get the FCM token from localStorage
        const fcmToken = localStorage.getItem('fcm_token');
        if (!fcmToken) {
          console.log('⚠️ NO FCM TOKEN FOUND IN STORAGE, NOTHING TO UNREGISTER');
          return;
        }
        
        console.log('🔥 FCM TOKEN FOUND, UNREGISTERING FROM BACKEND');
        console.log('📱 Device ID:', deviceId);
        console.log('🔑 Token Length:', fcmToken.length);
        
        // Unregister the token with the backend
        const endpoint = 'authapi/auths/logout';
        console.log('📡 API Endpoint:', endpoint);
        
        const data = {
          deviceId: deviceId,
          fcmToken: fcmToken
        };
        
        // Log environment info for debugging
        console.log('🔐 ENVIRONMENT INFO:');
        console.log('🔐 API Base URL:', DevURL);
        console.log('🔐 Full Endpoint:', `${DevURL}${endpoint}`);
        console.log('📦 Sending data:', JSON.stringify(data));
        
        // Log the full URL that will be called
        const fullUrl = `${DevURL}${endpoint}`;
        console.log('🔗 FULL API URL:', fullUrl);
        
        try {
          const response = await axoisAPI.postwithToken(endpoint, data);
          console.log('✅ FCM TOKEN UNREGISTERED SUCCESSFULLY:', response?.status);
          
          // Clear FCM token from localStorage
          localStorage.removeItem('fcm_token');
          localStorage.removeItem('fcm_token_registered');
          console.log('🗑️ FCM TOKEN REMOVED FROM LOCAL STORAGE');
        } catch (apiError) {
          console.error('❌ API REQUEST FAILED DURING LOGOUT:', apiError);
          // Still remove the token from localStorage even if API call fails
          localStorage.removeItem('fcm_token');
          localStorage.removeItem('fcm_token_registered');
          console.log('🗑️ FCM TOKEN REMOVED FROM LOCAL STORAGE DESPITE API ERROR');
        }
      } else {
        console.log('💻 BROWSER DETECTED - SKIPPING FCM TOKEN UNREGISTRATION');
      }
    } catch (err) {
      const error = err as Error;
      console.error('❌ ERROR UNREGISTERING TOKEN AT LOGOUT:', error);
      
      if (error instanceof Error) {
        console.error('❌ Error name:', error.name);
        console.error('❌ Error message:', error.message);
        console.error('❌ Error stack:', error.stack);
      }
    }
  }
  
  // Request permission check - can be called from UI to prompt user
  static async checkPermissions(): Promise<boolean> {
    try {
      if (this.isNativePlatform()) {
        // Native platform implementation
        const permissionStatus = await PushNotifications.checkPermissions();
        return permissionStatus.receive === 'granted';
      } else {
        // Web browser implementation
        if (!('Notification' in window)) {
          return false;
        }
        return Notification.permission === 'granted';
      }
    } catch (error) {
      console.error('Error checking permissions:', error);
      return false;
    }
  }
  
  // Clear all delivered notifications
  static async clearNotifications(): Promise<void> {
    try {
      if (this.isNativePlatform()) {
        // Clear notifications on native platform
        await PushNotifications.removeAllDeliveredNotifications();
        console.log('All native notifications cleared');
      } else {
        // For web platforms, there's no standard way to clear notifications
        // The best we can do is close notifications we've created
        console.log('Web platform detected, no action needed for clearing notifications');
      }
    } catch (error) {
      console.error('Error clearing notifications:', error);
    }
  }
  
  // Specifically for post-login FCM token registration
  static async registerSavedTokenAfterLogin(): Promise<void> {
    if (!this.isNativePlatform()) {
      return; // Only relevant for native platforms with FCM
    }
    
    try {
      // Check if we have a saved token from earlier
      const savedToken = localStorage.getItem('fcm_token');
      
      if (savedToken) {
        console.log('🔄 FOUND SAVED FCM TOKEN AFTER LOGIN - ATTEMPTING REGISTRATION');
        await this.registerTokenWithBackend(savedToken);
      } else {
        console.log('❓ NO SAVED FCM TOKEN FOUND AFTER LOGIN');
      }
    } catch (error) {
      console.error('Error registering saved token after login:', error);
    }
  }
}